import { format } from 'date-fns';

import { Maybe } from '@/types';
import { OfficeInfoType } from '@/services/useFacilities';
import { AvailableSlot, NamedAttribute } from '@/graphql/__generated/sdk';
import { BffBrandType, FacilityBrand } from '@/types/generated';
import { getBffAvailabilityByLocation } from '@/services/getBffAvailabilityByLocation';

export const getAttributeByName = (
	officeInfo: Maybe<OfficeInfoType>,
	name: string
): NamedAttribute | undefined | null => {
	return officeInfo?.attributes?.find((attr) => {
		return attr?.name === name;
	});
};

export const getIsScheduleTypeOpen = (
	officeInfo: Maybe<OfficeInfoType>,
	facilityBrand: FacilityBrand | BffBrandType
): boolean => {
	return facilityBrand === FacilityBrand.Lovet && getAttributeByName(officeInfo, 'Schedule Type')?.value === 'Open';
};

export function filterSlotsUniqueByTime(availableSlots: AvailableSlot[], limit?: number) {
	if (!availableSlots) return {};
	const slotsByDate: Record<string, AvailableSlot[]> = {};

	availableSlots.forEach((slot) => {
		if (slot) {
			if (!slotsByDate[slot.date]) {
				slotsByDate[slot.date] = [];
			}
			const isDuplicate = slotsByDate[slot.date].some((s) => s.date === slot.date && s.time === slot.time);
			if (!isDuplicate) {
				slotsByDate[slot.date].push(slot);
			}
		}
	});

	if (limit) {
		Object.keys(slotsByDate).forEach((date) => {
			slotsByDate[date] = slotsByDate[date].slice(0, limit);
		});
	}

	// Re-insert keys in ascending date order regardless of the API response order.
	return Object.fromEntries(
		Object.entries(slotsByDate).sort(([a], [b]) => a.localeCompare(b)),
	);
}

export type TFetchTimesReturnType = Record<
	string,
	{
		date: string;
		slots: AvailableSlot[];
		isDisabled: boolean;
	}
>;

export const fetchTimes = async ({
	brand,
	facilityId,
	url,
	appName,
	appVersion,
	startDate,
	endDate,
}: {
	brand: BffBrandType;
	facilityId: string;
	url: string;
	appName: string;
	appVersion: string;
	startDate?: Date;
	endDate?: Date;
}): Promise<TFetchTimesReturnType | undefined> => {
	try {
		const facilityIdShort = facilityId.replace(/^LV_/, '');
		const availabilityData = await getBffAvailabilityByLocation(
			brand,
			facilityIdShort,
			url,
			appName,
			appVersion,
			startDate,
			endDate
		);

		if (availabilityData?.availabilityByLocation?.availableSlots) {
			const allSlots = availabilityData.availabilityByLocation.availableSlots as AvailableSlot[];
			const slotsByDateGrouped = filterSlotsUniqueByTime(allSlots);
			const result: TFetchTimesReturnType = {};

			// Get all dates in the range
			const start = startDate || new Date();
			const end = endDate || new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);

			for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
				const dateKey = format(d, 'yyyy-MM-dd');
				const dateSlots = slotsByDateGrouped[dateKey] || [];

				const formattedDateString = d.toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				});

				result[dateKey] = {
					date: formattedDateString,
					slots: dateSlots,
					isDisabled: dateSlots.length === 0,
				};
			}

			return result;
		}
		return;
	} catch (error) {
		console.error('Error fetching times:', error);
		return;
	}
};

export function formatDisplayTime(time: string): string {
	const [hours, minutes] = time.split(':');
	const formattedHours = Number.parseInt(hours, 10) % 12 || 12; // Convert to 12-hour format
	const ampm = Number.parseInt(hours, 10) < 12 ? 'AM' : 'PM';
	return `${formattedHours}:${minutes} ${ampm}`;
}

export const getWeekOut = (startDate: Date) => {
	const weekFromStart = new Date(startDate);
	weekFromStart.setDate(startDate.getDate() + 6);

	const formatDate = (date: Date) => {
		const month = date.toLocaleString('en-US', { month: 'short' });
		const day = date.getDate();
		return `${month} ${day}`;
	};

	return `${formatDate(startDate)} - ${formatDate(weekFromStart)}`;
};
